// Quote Builder — fuzzy match engine + favourites system.
//
// Verbatim port of source kos-qb-v3.js:
//   • normalise / fuzzyScore / subseqScore / lev1 / fuzzyFilter / highlight
//     (lines 427–544)
//   • getFavs / bumpFav / favScore (lines 401–412) + FAVS_KEY (line 43)
//
// Used by the pickers' combo dropdowns (door range, handle, storage,
// lighting, appliance) for fuzzy-search ranking and favourites boosting.

const FAVS_KEY = "kos_qb_favourites_v3";

// ── Favourites ────────────────────────────────────────────────────────────

interface FavMap { [key: string]: number; }

export function getFavs(): FavMap {
  try {
    const raw = localStorage.getItem(FAVS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === "object") return parsed as FavMap;
  } catch { /* ignore */ }
  return {};
}

export function bumpFav(productKey: string | undefined | null): void {
  if (!productKey) return;
  const f = getFavs();
  f[productKey] = (f[productKey] ?? 0) + 1;
  try { localStorage.setItem(FAVS_KEY, JSON.stringify(f)); } catch { /* ignore */ }
}

export function favScore(productKey: string | undefined | null): number {
  if (!productKey) return 0;
  const f = getFavs();
  const n = Number(f[productKey] ?? 0);
  return n > 0 ? Math.log(1 + n) * 25 : 0;
}

// ── Normalisation ─────────────────────────────────────────────────────────

export function normalise(s: unknown): string {
  return String(s ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9 ]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// ── Score primitives ──────────────────────────────────────────────────────

/** Subsequence score (0..40) — chars in order. */
export function subseqScore(q: string, t: string): number {
  let qi = 0, run = 0, runs = 0, hits = 0;
  for (let i = 0; i < t.length && qi < q.length; i++) {
    if (t.charAt(i) === q.charAt(qi)) {
      qi++; hits++; run++;
      if (run > runs) runs = run;
    } else run = 0;
  }
  if (qi !== q.length) return 0;
  return Math.min(40, hits * 6 + runs * 3);
}

/** Levenshtein distance ≤ 1 (cheap; substitution, insertion, deletion). */
export function lev1(a: string, b: string): boolean {
  if (a === b) return true;
  const la = a.length, lb = b.length;
  if (Math.abs(la - lb) > 1) return false;
  if (la === lb) {
    let d = 0;
    for (let i = 0; i < la; i++) if (a.charAt(i) !== b.charAt(i)) {
      d++;
      if (d > 1) return false;
    }
    return true;
  }
  const s = la < lb ? a : b;
  const l = la < lb ? b : a;
  let i2 = 0, j2 = 0, ed = 0;
  while (i2 < s.length && j2 < l.length) {
    if (s.charAt(i2) !== l.charAt(j2)) {
      ed++;
      if (ed > 1) return false;
      j2++;
    } else { i2++; j2++; }
  }
  return true;
}

/** Score a single (query, text) pair. Higher = better. */
export function fuzzyScore(query: string, text: string): number {
  if (!query) return 0;
  const Q = normalise(query);
  const T = normalise(text);
  if (!T) return 0;
  if (T === Q) return 1000;
  if (T.indexOf(Q) === 0) return 500 + Math.max(0, 50 - (T.length - Q.length));
  const qTokens = Q.split(" ").filter(Boolean);
  const tWords = T.split(" ");
  let total = 0;
  for (const qt of qTokens) {
    let best = 0;
    for (const tw of tWords) {
      let s = 0;
      if (tw === qt) s = 320;
      else if (tw.indexOf(qt) === 0) s = 240;
      else if (tw.indexOf(qt) >= 0) s = 160;
      else if (lev1(qt, tw)) s = 130;
      else {
        const sub = subseqScore(qt, tw);
        if (sub > 0) s = 60 + sub;
      }
      if (s > best) best = s;
    }
    if (best === 0) {
      if (T.indexOf(qt) >= 0) best = 100;
      else {
        const subT = subseqScore(qt, T);
        if (subT > 0) best = 30 + subT;
      }
    }
    if (best === 0) return 0;
    total += best;
  }
  return total - Math.max(0, Math.floor((T.length - Q.length) / 8));
}

// ── Filter ────────────────────────────────────────────────────────────────

export interface FuzzyOpts<T> {
  threshold?: number;
  limit?: number;
  favKey?: (item: T) => string;
}

export interface FuzzyHit<T> { item: T; score: number; }

export function fuzzyFilter<T>(
  query: string,
  items: T[],
  getText: (item: T) => string,
  opts: FuzzyOpts<T> = {},
): FuzzyHit<T>[] {
  const threshold = opts.threshold ?? 30;
  const limit = opts.limit ?? 50;
  if (!query || !query.trim()) {
    const sorted = items.slice();
    if (opts.favKey) {
      sorted.sort((a, b) => favScore(opts.favKey!(b)) - favScore(opts.favKey!(a)));
    }
    return sorted.slice(0, limit).map((x) => ({ item: x, score: 0 }));
  }
  const hits: FuzzyHit<T>[] = [];
  for (const it of items) {
    const txt = getText(it);
    let sc = fuzzyScore(query, txt);
    if (opts.favKey) sc += favScore(opts.favKey(it));
    if (sc >= threshold) hits.push({ item: it, score: sc });
  }
  hits.sort((a, b) => b.score - a.score);
  return hits.slice(0, limit);
}

// ── Highlight ─────────────────────────────────────────────────────────────

function escHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** Wrap matching characters in a `<em>` tag for UI highlighting. */
export function highlight(text: string, query: string): string {
  if (!text) return "";
  if (!query) return escHtml(text);
  const Q = normalise(query);
  const T = text;
  const Tl = T.toLowerCase();
  let out = "";
  let i = 0;
  while (i < T.length) {
    const idx = Tl.indexOf(Q, i);
    if (idx < 0) { out += escHtml(T.slice(i)); break; }
    out += escHtml(T.slice(i, idx));
    out += `<em class="qb-fuzzy-hit">${escHtml(T.slice(idx, idx + Q.length))}</em>`;
    i = idx + Q.length;
  }
  return out;
}
